import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { CaretDownIcon, XIcon } from '@phosphor-icons/react'
import { AllGuildTribeHeader, AllGuildTypes } from '../../types/Guilds'

export interface GuildFormData {
  name: string
  type: string
}

interface GuildModalProps {
  show: boolean
  mode: 'add' | 'edit'
  initialValues?: GuildFormData
  onClose: () => void
  onSubmit: (data: GuildFormData) => Promise<boolean | void> | boolean | void
}

const GUILD_TYPE_OPTIONS = Object.keys(AllGuildTypes).map(key => ({
  value: AllGuildTypes[key],
  label: AllGuildTribeHeader[key],
}))

export const GuildModal = ({
  show,
  mode,
  initialValues,
  onClose,
  onSubmit,
}: GuildModalProps) => {
  const [name, setName] = useState('')
  const [type, setType] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = mode === 'edit'

  useEffect(() => {
    if (!show) {
      setIsSubmitting(false)
      return
    }

    setName(initialValues?.name ?? '')
    setType(initialValues?.type ?? '')
  }, [show, mode])

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const canSave = name.trim().length > 0 && type !== ''

  const handleSubmit = async () => {
    if (!canSave || isSubmitting) return
    setIsSubmitting(true)
    try {
      const result = await onSubmit({ name: name.trim(), type })
      if (result === false) return
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="guild-modal-dialog"
      contentClassName="guild-modal-content"
    >
      <div className="guild-modal">
        <div className="guild-modal__header">
          <h2 className="guild-modal__title">
            {isEditMode ? 'Update Guild' : 'Add New Guild'}
          </h2>
          <button
            className="guild-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <XIcon size={20} weight="regular" />
          </button>
        </div>

        <div className="guild-modal__content">
          <div className="guild-modal__fields">
            <div className="guild-modal__field">
              <label className="guild-modal__label guild-modal__label--required">
                Name
              </label>
              <input
                type="text"
                className="guild-modal__input"
                placeholder="Guild name"
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={25}
              />
            </div>

            <div className="guild-modal__field">
              <label className="guild-modal__label guild-modal__label--required">
                Type
              </label>
              <div className="guild-modal__select-wrap">
                <select
                  className="guild-modal__select"
                  value={type}
                  onChange={e => setType(e.target.value)}
                >
                  <option value="" disabled>
                    Select a type
                  </option>
                  {GUILD_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <CaretDownIcon
                  size={14}
                  weight="regular"
                  className="guild-modal__select-caret"
                />
              </div>
            </div>
          </div>

          <div className="guild-modal__footer">
            <button
              className="guild-modal__btn guild-modal__btn--cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="guild-modal__btn guild-modal__btn--save"
              onClick={handleSubmit}
              disabled={!canSave || isSubmitting}
            >
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default GuildModal
